import React, { ReactNode } from 'react'

import useAuth0ClientConfig from '../../../lib/auth0/use-auth0-client-config'
import Paragraph from '../../../scentregroup-ui/components/paragraph'
import {
  ValidateSignUpEmailError,
  ValidateSignUpEmail,
} from '../../../lib/apiService'
import OnboardingForm from '../onboarding-form'
import style from './index.module.scss'
import SignUpFormFields from './sign-up-form-fields'
import useBlurValidation from '../../../lib/formValidation/use-blur-validation'
import useQueryContext from '../../../lib/queryParams/use-query-context'

export interface OnChangeEmail {
  (email: string): void
}

export interface Values extends Record<string, unknown> {
  email: string
}

type OnSuccessData = Values
export interface OnSuccess {
  (data: OnSuccessData): void
}

interface OnFailureData extends OnSuccessData {
  errors: ValidateSignUpEmailError[]
}
export interface OnFailure {
  (data: OnFailureData): void
}

export interface RenderLoginLink {
  (className?: string): ReactNode
}

interface Props {
  draftEmail: string
  onChangeEmail: OnChangeEmail
  onSuccess: OnSuccess
  onFailure: OnFailure
  validateSignUpEmail: ValidateSignUpEmail
  renderLoginLink: RenderLoginLink
  googleLogin?: () => void
  appleLogin?: () => void
}

const SignUpForm: React.FC<Props> = ({
  draftEmail,
  onChangeEmail,
  onSuccess,
  onFailure,
  validateSignUpEmail,
  renderLoginLink,
  googleLogin,
  appleLogin,
}) => {
  const TITLE_CHECKOUT = 'Become a Westfield member to complete your order'
  const TITLE_MIGRATE = 'Please migrate your account'
  const TITLE_DEFAULT = 'Become a member or login'

  const INTRO_CHECKOUT = `Your free membership unlocks benefits when shopping in-centre.`
  const INTRO_MIGRATE =
    'You have attempted to login using an older account that requires migration, please enter your email below to continue.'
  const INTRO_DEFAULT = `Please enter your email address. We'll send a verification code to your inbox.`

  const { enableBlurValidation, disableBlurValidation } = useBlurValidation()
  const { isContextCheckout, isContextMigrate } = useQueryContext()
  const { consumerUrl } = useAuth0ClientConfig()

  function getTitle() {
    if (isContextMigrate) {
      return TITLE_MIGRATE
    }

    if (isContextCheckout) {
      return TITLE_CHECKOUT
    }

    return TITLE_DEFAULT
  }

  function getIntro() {
    if (isContextMigrate) {
      return INTRO_MIGRATE
    }

    if (isContextCheckout) {
      return INTRO_CHECKOUT
    }

    return INTRO_DEFAULT
  }

  return (
    <OnboardingForm
      title={getTitle()}
      intro={<Paragraph className={style.intro}>{getIntro()}</Paragraph>}
      form={
        <SignUpFormFields
          draftEmail={draftEmail}
          onChangeEmail={onChangeEmail}
          onSuccess={onSuccess}
          onFailure={onFailure}
          validateSignUpEmail={validateSignUpEmail}
          googleLogin={googleLogin}
          appleLogin={appleLogin}
        />
      }
      footer={
        <>
          <Paragraph className={style.terms}>
            By continuing, you agree to our{' '}
            <span
              onMouseEnter={disableBlurValidation}
              onMouseLeave={enableBlurValidation}
            >
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={`${consumerUrl}/terms-and-conditions`}
              >
                Terms and Conditions
              </a>
            </span>{' '}
            and{' '}
            <span
              onMouseEnter={disableBlurValidation}
              onMouseLeave={enableBlurValidation}
            >
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={`${consumerUrl}/privacy-policy`}
              >
                Privacy Statement
              </a>
            </span>
          </Paragraph>
        </>
      }
    />
  )
}

export default SignUpForm
